import FmdGoodIcon from "@mui/icons-material/FmdGood";
import TripOriginIcon from "@mui/icons-material/TripOrigin";
import { Box, Stack, Typography } from "@mui/material";
import { Link } from "react-router-dom";

import { MultiShipmentChildShipmentChip } from "components/MultiShipmentChildShipmentChip";
import { PrivateShipmentChip } from "components/PrivateShipmentChip";
import { ShipmentStatusBadge } from "components/ShipmentStatusBadge";
import { ShipmentTypeChip } from "components/ShipmentTypeChip";
import { ShipmentCustomNameText } from "components/Texts/ShipmentCustomNameText";
import { ShipmentIDText } from "components/Texts/ShipmentIDText";
import { ShipmentLocationText } from "components/Texts/ShipmentLocationText";
import {
  getServiceTypeDisplayLabel,
  scrollMainContentToTop,
  toCityProvinceCountryAndZipReadablePointLocation,
} from "utils";

import { MultiShipmentProjectLoadsAvailableLoadsLabel } from "../MultiShipmentProjectLoadsAvailableLoadsLabel";
import { QuoteCountChip } from "../QuoteCountChip";
import { SharedShipmentChip } from "../SharedShipmentChip";

interface Props {
  shipment: ShipperCondensedShipment;
  selected: boolean;
  select: (id: number) => void;
  selectionIsDisabled: boolean;
}

export function CompactShipmentPreview(props: Props) {
  const { shipment, selected, select, selectionIsDisabled } = props;
  const shipmentInfo = shipment.info;
  const equipmentTypes = shipment?.equipments;
  const bg = selected ? "#e3e3e3" : "white";
  const getEquipmentTypesDisplay = () => {
    let equipmentTypesDisplay = "-";
    if (equipmentTypes.length > 1) {
      equipmentTypesDisplay = equipmentTypes[0]?.name;
      /*
        This will result in a string that looks like:
        "Dry Van (+ 2 more)"
        Indicating that the shipment has a dry van equipment specified, plus 2 additional types.
      */
      equipmentTypesDisplay = `${equipmentTypesDisplay} (+ ${equipmentTypes.length - 1} more)`;
    } else if (equipmentTypes.length === 1) {
      equipmentTypesDisplay = equipmentTypes[0]?.name;
    }
    return equipmentTypesDisplay;
  };

  return (
    <Box
      component={selectionIsDisabled ? "div" : Link}
      to={selectionIsDisabled ? undefined : `/shipments/${shipmentInfo.id}`}
      p="1rem"
      sx={{
        display: "block",
        textDecoration: "none",
        color: "inherit",
        "&:hover": {
          bgcolor: selectionIsDisabled ? "#959696" : "#f8f8f8",
          cursor: selectionIsDisabled ? "wait" : "pointer",
        },
        background: bg,
      }}
      onClick={(e: React.MouseEvent) => {
        if (!selectionIsDisabled) {
          select(shipmentInfo.id);
          scrollMainContentToTop();
        } else {
          e.preventDefault();
        }
      }}
    >
      <Box mb="0.5rem">
        <ShipmentIDText shipment={shipment} />
        {shipment?.custom_name != null && (
          <ShipmentCustomNameText data={shipment.custom_name} />
        )}

        <MultiShipmentProjectLoadsAvailableLoadsLabel shipment={shipment} />
        <Stack direction="row" spacing={1}>
          <Typography sx={{ textTransform: "capitalize" }} variant="body1">
            {" "}
            {getEquipmentTypesDisplay()}{" "}
          </Typography>
          <Typography>|</Typography>
          <Typography sx={{ textTransform: "capitalize" }}>
            {getServiceTypeDisplayLabel(shipmentInfo.service)}
          </Typography>
        </Stack>
        {shipmentInfo.type === "MULTI_SHIPMENT_PROJECT" && (
          <Typography variant="body1">Multi Shipment Project</Typography>
        )}
      </Box>

      <Stack
        direction="row"
        alignItems="center"
        sx={{ flexWrap: "wrap", gap: 1 }}
      >
        <ShipmentStatusBadge status={shipment?.shipment_status} size="small" />
        <SharedShipmentChip shipment={shipment} size="small" />
        <QuoteCountChip
          quoteCount={
            shipment?.condensed_quotes.filter(
              (q) => q.hidden_at == null && !q.carrier_is_blocked,
            ).length
          }
          size="small"
        />
        <ShipmentTypeChip shipmentType={shipment?.info.type} />
        {shipment && (
          <MultiShipmentChildShipmentChip
            isMultiShipmentChildShipment={
              shipment?.info.type === "MULTI_SHIPMENT_PROJECT_CHILD"
            }
          />
        )}
        <PrivateShipmentChip
          isPrivate={shipment?.is_private ?? false}
          isProject={shipment?.info?.type === "MULTI_SHIPMENT_PROJECT"}
          color="info"
        />
      </Stack>
      <Stack spacing={1} mt="1rem">
        <ShipmentLocationText
          data={toCityProvinceCountryAndZipReadablePointLocation(
            shipment.first_point.point_location,
          )}
          icon={
            <TripOriginIcon sx={{ fontSize: "0.9rem", marginRight: "5px" }} />
          }
        />
        <ShipmentLocationText
          data={toCityProvinceCountryAndZipReadablePointLocation(
            shipment?.last_point.point_location,
          )}
          icon={<FmdGoodIcon sx={{ fontSize: "0.9rem", marginRight: "5px" }} />}
        />
      </Stack>
    </Box>
  );
}
